import { extent, mean, median } from 'd3-array';
import {
    scaleLinear,
    scaleOrdinal,
    ScaleOrdinal,
    scaleQuantile,
    ScaleQuantile,
    scaleSequential,
    ScaleSequential,
} from 'd3-scale';
import {
    interpolateBrBG,
    interpolateGnBu,
    interpolateRdBu,
    interpolateRdPu,
    interpolateSpectral,
    interpolateViridis,
    interpolateYlOrBr,
    schemeGnBu,
    schemeRdPu,
    schemeYlOrBr,
} from 'd3-scale-chromatic';
import { Feature, Geometry } from 'geojson';
import { useMemo } from 'react';
import { getFlatValues } from '../helpers/get-flat-values';
import { Indicator } from '../types';

import { assignKommunColors } from '../helpers/assign-kommun-colors';
import { ckmeans } from 'simple-statistics';
import { featureIsIncludedInCurrentFilter } from '../helpers/filter';
import { DEFAULT_COLOR_SCHEME } from '../constants';

export const colorScales: { [name: string]: any } = {
    interpolateYlOrBr,
    schemeYlOrBr,
    interpolateSpectral,
    interpolateBrBG,
    interpolateRdBu,
    interpolateGnBu,
    interpolateRdPu,
    schemeGnBu,
    schemeRdPu,
    interpolateViridis,
};

const UNKNOWN_KOMMUN_COLOR = '#bab0ab';

function getKommunScale(features: Feature<Geometry>[]) {
    const coordsByKommun = new Map<string, [number, number][]>();
    for (const f of features) {
        const kommunnamn = f.properties?.kommunnamn;
        if (!kommunnamn) continue;
        const coords = coordsByKommun.get(kommunnamn) ?? [];
        if (f.properties?.labelCoords) coords.push(f.properties.labelCoords);
        coordsByKommun.set(kommunnamn, coords);
    }

    const colors = assignKommunColors(
        Array.from(coordsByKommun, ([kommunnamn, coords]) => ({
            kommunnamn,
            coords:
                coords.length > 0
                    ? ([
                          mean(coords, (c) => c[0])!,
                          mean(coords, (c) => c[1])!,
                      ] as [number, number])
                    : undefined,
        }))
    );

    return scaleOrdinal<string, string>()
        .domain(Array.from(colors.keys()))
        .range(Array.from(colors.values()))
        .unknown(UNKNOWN_KOMMUN_COLOR);
}

export type ScaleType = 'linear' | 'naturalbreaks';
export type ColorType = 'sequential' | 'diverging';
type Args = {
    data: Feature<Geometry>[];
    cVar: Indicator | null;
    scaleType?: ScaleType;
    colorType?: ColorType;
    colorScheme?: string;
    featureFilter: string[];
    zoomToFiltered?: boolean;
};

const nGroups = 5;

export type ColorScaleReturnType =
    | { scaleType: 'naturalbreaks'; cScale: ScaleQuantile<string> }
    | {
          scaleType: 'linear';
          cScale: DivergingColorScale | ScaleSequential<string>;
      }
    | { scaleType: 'ordinal'; cScale: ScaleOrdinal<string, string, string> };

export function useColorScale({
    data,
    cVar,
    scaleType = 'naturalbreaks',
    colorType = 'sequential',

    featureFilter,
    zoomToFiltered,
    colorScheme,
}: Args): ColorScaleReturnType {
    const features = useMemo(
        () =>
            data.filter((d) => {
                if (!zoomToFiltered) return true;
                return featureIsIncludedInCurrentFilter(d, featureFilter);
            }),
        [data, featureFilter, zoomToFiltered]
    );

    // Only kommuner that are drawn need distinct colors
    const kommunScale = useMemo(
        () =>
            cVar
                ? null
                : getKommunScale(
                      data.filter((d) =>
                          featureIsIncludedInCurrentFilter(d, featureFilter)
                      )
                  ),
        [data, featureFilter, cVar]
    );

    const cValueRange = useMemo(() => {
        if (!cVar) {
            return null;
        }

        switch (scaleType) {
            case 'naturalbreaks': {
                const flatValues = getFlatValues<number>(features, cVar.id);
                const _nGroups =
                    flatValues.length <= nGroups
                        ? flatValues.length - 1
                        : nGroups;
                let _extent = extent<number>(flatValues) as [number, number];

                // Apply min/max overrides from indicator settings
                // When set, these define the exact scale domain (window)
                if (cVar.minValue != null) {
                    _extent[0] = cVar.minValue;
                }
                if (cVar.maxValue != null) {
                    _extent[1] = cVar.maxValue;
                }

                const jenks = ckmeans(flatValues, _nGroups).map((v) => v.pop());
                return [_extent[0], ...jenks];
            }
            case 'linear': {
                const flatValues = getFlatValues<number>(features, cVar.id);
                let range = extent<number>(flatValues) as [number, number];

                // Apply min/max overrides from indicator settings
                // When set, these define the exact scale domain (window)
                if (cVar.minValue != null) {
                    range[0] = cVar.minValue;
                }
                if (cVar.maxValue != null) {
                    range[1] = cVar.maxValue;
                }

                return range;
            }
        }
    }, [cVar, featureFilter, zoomToFiltered, scaleType, features]);

    if (!cVar) {
        return { cScale: kommunScale!, scaleType: 'ordinal' };
    }

    switch (scaleType) {
        case 'naturalbreaks': {
            let scheme = colorScales[
                colorScheme ?? DEFAULT_COLOR_SCHEME
            ] as string[];
            let range = cValueRange!;

            // Fallback to avoid cases where user has picked a linear scaletype
            // and then switches to a discrete scale type
            if (!Array.isArray(scheme)) {
                scheme = colorScales[DEFAULT_COLOR_SCHEME] as string[];
            }

            const cScale = scaleQuantile(scheme[nGroups]).domain(range);
            return { cScale, scaleType: 'naturalbreaks' };
        }
        case 'linear': {
            let scheme = colorScales[colorScheme ?? 'interpolateGnBu'] as (
                t: number
            ) => string;
            let range = cValueRange as number[];
            let midpoint: number;

            if (colorType === 'diverging') {
                scheme = colorScales[colorScheme ?? 'interpolateRdBu'] as (
                    t: number
                ) => string;

                // Fallback to avoid cases where user has picked a discrete scaletype
                // and then switches to a diverging color scale
                if (typeof scheme !== 'function') {
                    scheme = interpolateRdBu;
                }

                if (typeof cVar.midPoint !== 'undefined') {
                    midpoint = cVar.midPoint;
                } else {
                    const flatValues = getFlatValues<number>(features, cVar.id);
                    const cVarMedian = median(flatValues);

                    if (!cVarMedian) {
                        throw new Error('Failed to get median');
                    }

                    midpoint = cVarMedian;
                }

                const c = createDivergingColorScale(
                    midpoint,
                    range[0],
                    range[1],
                    scheme
                );

                return { cScale: c, scaleType: 'linear' };
            }

            const cScale = scaleSequential(scheme).domain(
                cValueRange as number[]
            );

            return { cScale, scaleType: 'linear' };
        }
    }
}

export type DivergingColorScale = {
    (value: number): string;
    domain: () => number[];
};

function createDivergingColorScale(
    midpoint: number,
    minValue: number,
    maxValue: number,
    colorInterpolator: (t: number) => string
): DivergingColorScale {
    // Create two separate scales for the negative and positive parts of the data
    const negativeScale = scaleLinear()
        .domain([minValue, midpoint])
        .range([0, 0.5]);

    const positiveScale = scaleLinear()
        .domain([midpoint, maxValue])
        .range([0.5, 1]);

    // Combine the two scales into a single custom color scale function
    function scale(value: number) {
        if (value < midpoint) {
            return colorInterpolator(negativeScale(value));
        } else {
            return colorInterpolator(positiveScale(value));
        }
    }

    scale.domain = () => [minValue, midpoint, maxValue];
    return scale;
}
